import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';
import { Button, ButtonVariant } from './Button';

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Description = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.88rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const fieldStyles = `
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  resize: vertical;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const TextField = styled.input`
  ${fieldStyles}
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

export interface PromptModalProps {
  isOpen: boolean;
  title?: string;
  /** Optional description shown above the field. */
  message?: React.ReactNode;
  /** Field label. */
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Disable confirm while the field is empty (default true). */
  required?: boolean;
  /** Textarea (default) vs single-line input. */
  multiline?: boolean;
  /** Destructive tone for the confirm button. */
  danger?: boolean;
  loading?: boolean;
  onConfirm: (value: string) => void;
  onClose: () => void;
}

/**
 * Design-system input dialog - replaces the native `window.prompt`. Collects a
 * single text value (reason, note, name…) and returns it via [onConfirm].
 */
export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  title = 'Informe um valor',
  message,
  label,
  placeholder,
  initialValue = '',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  required = true,
  multiline = true,
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}) => {
  const [value, setValue] = useState(initialValue);

  // Reset the field to the initial value each time the modal opens.
  useEffect(() => {
    if (isOpen) setValue(initialValue);
  }, [isOpen, initialValue]);

  const confirmVariant: ButtonVariant = danger ? 'danger' : 'primary';
  const disabled = loading || (required && value.trim().length === 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="460px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={() => onConfirm(value.trim())} disabled={disabled} isLoading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <Description>{message}</Description>}
      {label && <FieldLabel>{label}</FieldLabel>}
      {multiline ? (
        <TextArea
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          autoFocus
        />
      ) : (
        <TextField
          value={value}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          autoFocus
        />
      )}
    </Modal>
  );
};

export default PromptModal;
